import React from 'react';
import { motion } from 'framer-motion';
import { Building, Server, Code, AlertCircle, Shield, Cookie, ExternalLink, Scale, Mail } from 'lucide-react';

const Mentions = () => {
  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 }
  };

  // Sections with icons
  const sections = [
    {
      title: "Informations légales",
      icon: <Building className="text-blue-400" size={24} />,
      content: (
        <>
          <p>Le présent site web est édité par :</p>
          <address className="mt-2 not-italic text-gray-300">
            <p>FACRORY By CFA Est Loiret</p>
            <p>3 rue Cyrille Robert</p>
            <p>45200 Montargis</p>
            <p>SIRET : [Numéro SIRET]</p>
            <p>Téléphone : 02 38 98 05 92</p>
            <p>Email : contact@cfa-montargis.com</p>
          </address>
          <p className="mt-4">Directeur de la publication : [Nom du directeur]</p>
        </>
      )
    },
    {
      title: "Hébergement",
      icon: <Server className="text-blue-400" size={24} />,
      content: (
        <>
          <p>Ce site est hébergé par :</p>
          <address className="mt-2 not-italic text-gray-300">
            <p>[Serveur Web du CFA]</p>
            <p>[Annexe - Villemandeur
              12 Rue des Frères Lumière, 45700 Villemandeur]</p>
            <p>Téléphone : [02 38 98 05 92]</p>
          </address>
        </>
      )
    },
    {
      title: "Propriété intellectuelle",
      icon: <Code className="text-blue-400" size={24} />,
      content: (
        <>
          <p>
            L'ensemble des éléments constituant ce site (textes, graphismes, logiciels, photographies, images, vidéos, sons, plans, logos, marques, etc.) est la propriété exclusive du CFA Est Loiret ou de ses partenaires. Ces éléments sont protégés par les lois relatives à la propriété intellectuelle.
          </p>
          <p className="mt-2">
            Toute reproduction ou représentation, totale ou partielle, de ce site ou de l'un quelconque des éléments qui le composent, sans l'autorisation expresse du CFA Est Loiret, est interdite et constituerait une contrefaçon sanctionnée par les articles L.335-2 et suivants du Code de la propriété intellectuelle.
          </p>
        </>
      )
    },
    {
      title: "Responsabilité",
      icon: <AlertCircle className="text-blue-400" size={24} />,
      content: (
        <>
          <p>
            Les informations contenues sur ce site sont aussi précises que possible et le site est périodiquement remis à jour. Cependant, des erreurs ou omissions peuvent survenir. L'utilisateur reconnaît utiliser ces informations sous sa responsabilité exclusive.
          </p>
          <p className="mt-2">
            Le CFA Est Loiret ne peut être tenu responsable des dommages directs et indirects consécutifs à l'accès au site.
          </p>
        </>
      )
    },
    {
      title: "Données personnelles",
      icon: <Shield className="text-blue-400" size={24} />,
      content: (
        <>
          <p>
            Le CFA Est Loiret s'engage à respecter la confidentialité des données personnelles communiquées par l'utilisateur du site et à les traiter dans le respect du Règlement Général sur la Protection des Données (RGPD).
          </p>
          <p className="mt-2">
            Pour plus d'informations sur la gestion des données personnelles, veuillez consulter notre <a href="/legal/rgpd" className="text-blue-400 hover:text-blue-300 underline">Politique de confidentialité</a>.
          </p>
        </>
      )
    },
    {
      title: "Cookies",
      icon: <Cookie className="text-blue-400" size={24} />,
      content: "Le site du CFA Est Loiret peut utiliser des cookies pour améliorer l'expérience de navigation de l'utilisateur. L'utilisateur peut désactiver ces cookies par l'intermédiaire des paramètres figurant au sein de son navigateur."
    },
    {
      title: "Liens hypertextes",
      icon: <ExternalLink className="text-blue-400" size={24} />,
      content: "Ce site peut contenir des liens hypertextes vers d'autres sites internet ou d'autres ressources disponibles sur Internet. Le CFA Est Loiret ne dispose d'aucun moyen pour contrôler les sites en connexion avec son site. Il ne répond pas de la disponibilité de tels sites et sources externes, ni ne la garantit. Il ne peut être tenu pour responsable de tout dommage, de quelque nature que ce soit, résultant du contenu de ces sites ou sources externes."
    },
    {
      title: "Droit applicable et juridiction compétente",
      icon: <Scale className="text-blue-400" size={24} />,
      content: "Les présentes mentions légales sont soumises à la loi française. En cas de litige, les tribunaux français seront compétents."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 text-white pt-24">
      <div className="container mx-auto px-4 py-8">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl font-bold mb-4 text-white">
            Mentions légales
          </h1>
          <div className="h-1 w-24 bg-blue-500 mx-auto rounded-full"></div>
        </motion.div>
        
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="max-w-4xl mx-auto"
        >
          {sections.map((section, index) => (
            <motion.div 
              key={index}
              variants={item}
              className="mb-12 bg-slate-800/50 p-6 rounded-lg border border-slate-700 backdrop-blur-sm hover:shadow-lg hover:shadow-blue-900/10 transition-all duration-300"
            >
              <div className="flex items-center mb-4">
                <div className="p-3 bg-slate-700/50 rounded-lg mr-4">
                  {section.icon}
                </div>
                <h2 className="text-2xl font-semibold text-white">
                  {index + 1}. {section.title}
                </h2>
              </div>
              <div className="pl-14 text-gray-300 leading-relaxed">
                {section.content}
              </div>
            </motion.div>
          ))}
          
          <motion.div 
            variants={item}
            className="mt-16 text-center text-gray-400 italic"
          >
            <p>Dernière mise à jour : {new Date().toLocaleDateString('fr-FR')}</p>
            <div className="flex justify-center mt-6 space-x-4">
              <a href="/legal/conditions" className="text-blue-400 hover:text-blue-300 underline">Conditions d'utilisation</a>
              <span className="text-gray-600">|</span>
              <a href="/legal/rgpd" className="text-blue-400 hover:text-blue-300 underline">Politique de confidentialité</a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default Mentions;